import React, { useState, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuLoaderCircle, LuPencil } from 'react-icons/lu';
import { doc, updateDoc } from 'firebase/firestore'; // Import updateDoc
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import { Field, SelectInput, TextInput } from '../../components/form/FormControls';
import useBudgetText from './budgetText';

// Initial empty state matching the AddIncomeModal structure for consistency
const initialFormData = {
    BütçeAdı: '',
    BütçeKaynak: '',
    BütçeSahibi: 'Şirket', 
    BütçeTipi: 'Gelir', 
    BütçeKategorisi: '',
    GiderTipi: 'Gelir', 
    ParaBirimi: 'TRY', 
    KDV: '0', 
    BütçeMiktarı: '',
    BütçeTarihi: '',
    BeklenenÖdemeTarihi: '', 
    ÖdemeDurumu: 'Alındı' 
};

/**
 * "Gelir Düzenle" (Tailwind): the summary fields of a record (Bütçe*). The
 * update logic is the old one; values outside the option lists stay selected.
 */
const EditIncomeModal = ({ isOpen, onClose, onSubmit, budgetData }) => { 
  const { t, language, label, statusLabel } = useBudgetText();
  const uid = useId();
  // Toasts and messages read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  // Initialize form data with initial state or budgetData if provided
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when budgetData is available or changes
  useEffect(() => {
    if (budgetData) {
      // Ensure all fields from budgetData are mapped, provide defaults if missing
      const populatedData = { ...initialFormData }; // Start with defaults
      for (const key in initialFormData) {
          if (budgetData.hasOwnProperty(key)) {
             populatedData[key] = budgetData[key];
          }
      }
      setFormData(populatedData);
    } else {
      // Reset if budgetData becomes null (e.g., modal closed and reopened)
      setFormData(initialFormData);
    }
  }, [budgetData, isOpen]); // Re-run if budgetData changes or modal reopens

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!budgetData || !budgetData.id) {
        toast.error(msg('incomeNotFound'));
        return;
    }
    setIsSubmitting(true);

    // Prepare data for Firestore update (exclude id, createdAt, etc.)
    const { id, createdAt, ...dataToUpdate } = formData;
    // Ensure type is set correctly even if form field was hidden
    dataToUpdate.GiderTipi = 'Gelir'; 
    dataToUpdate.BütçeTipi = 'Gelir';

    const budgetRef = doc(db, 'Budgets', budgetData.id);

    try {
      await updateDoc(budgetRef, dataToUpdate);

      toast.success(msg('incomeUpdated'));
      onSubmit(budgetData.id, dataToUpdate); // Pass ID and updated data back
      onClose(); // Close modal on success

    } catch (error) {
      console.error('Error updating income:', error);
      toast.error(msg('incomeUpdateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!budgetData) return null;

  const formId = `${uid}-form`;
  const choose = label('form.choose');
  const options = (values) => values.map((value) => ({ value, label: value }));
  // A stored value outside these lists (e.g. an e-invoice title) stays selected instead of blocking the form
  const select = (name, values, extra = {}) => (
    <SelectInput
      id={`${uid}-${name}`}
      name={name}
      value={formData[name] || ''}
      onChange={handleChange}
      required
      current={formData[name] || ''}
      options={options(values)}
      {...extra}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={label('form.titleEditIncome')}
      subtitle={budgetData['BütçeAdı'] || undefined}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{label('common.cancel')}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={isSubmitting} className="max-sm:tw-flex-1">
            {isSubmitting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={3} />}
            {isSubmitting ? label('form.updating') : label('form.updateIncome')}
          </Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('form.incomeName')} htmlFor={`${uid}-BütçeAdı`} required className="sm:tw-col-span-2">
          <TextInput id={`${uid}-BütçeAdı`} name="BütçeAdı" value={formData.BütçeAdı || ''} onChange={handleChange} required placeholder={label('form.incomeNamePlaceholder')} />
        </Field>
        <Field label={label('form.incomeSource')} htmlFor={`${uid}-BütçeKaynak`} required>
          {select('BütçeKaynak', ['Satışlar', 'Hizmetler', 'Yatırımlar', 'Diğer'], { placeholder: choose })}
        </Field>
        <Field label={label('form.incomeCategory')} htmlFor={`${uid}-BütçeKategorisi`} required>
          {select('BütçeKategorisi', ['Ana Gelir', 'Yan Gelir', 'Faiz Geliri'], { placeholder: choose })}
        </Field>
        <Field label={label('columns.amount')} htmlFor={`${uid}-BütçeMiktarı`} required>
          <TextInput id={`${uid}-BütçeMiktarı`} type="number" name="BütçeMiktarı" value={formData.BütçeMiktarı || ''} onChange={handleChange} required placeholder={label('form.amountPlaceholder')} step="0.01" className="tw-tabular-nums" />
        </Field>
        <Field label={label('columns.currency')} htmlFor={`${uid}-ParaBirimi`} required>
          {select('ParaBirimi', ['TRY', 'USD', 'EUR'])}
        </Field>
        <Field label={label('form.incomeDate')} htmlFor={`${uid}-BütçeTarihi`} required>
          <TextInput id={`${uid}-BütçeTarihi`} type="date" name="BütçeTarihi" value={formData.BütçeTarihi || ''} onChange={handleChange} required />
        </Field>
        <Field label={label('form.status')} htmlFor={`${uid}-ÖdemeDurumu`} required>
          {select('ÖdemeDurumu', ['Alındı', 'Beklemede'], { options: ['Alındı', 'Beklemede'].map((value) => ({ value, label: statusLabel(value) })) })}
        </Field>
      </form>
    </Modal>
  );
};

EditIncomeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  budgetData: PropTypes.object,
};

export default EditIncomeModal;
